import { useMemo, useState } from "react";
import { Target } from "lucide-react";
import "./GoalCalculator.css";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.round(n));

/** Reverse SIP: monthly P needed to reach FV */
function monthlyForGoal(FV, annualRate, years) {
  const r = annualRate / 12 / 100;
  const n = years * 12;
  if (n <= 0) return 0;
  if (r === 0) return FV / n;
  return FV / (((Math.pow(1 + r, n) - 1) / r) * (1 + r));
}

export default function GoalCalculator() {
  const [goal, setGoal] = useState(5000000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);

  const { monthly, invested, gains } = useMemo(() => {
    const m = monthlyForGoal(goal, rate, years);
    const inv = m * years * 12;
    return { monthly: m, invested: inv, gains: goal - inv };
  }, [goal, rate, years]);

  return (
    <section className="calc-card" id="goal">
      <div className="calc-card__head">
        <div className="calc-card__icon">
          <Target size={22} />
        </div>
        <div>
          <h2>Goal Calculator</h2>
          <p>Find the monthly SIP needed to reach a target corpus.</p>
        </div>
      </div>

      <div className="calc-layout">
        <div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="goal-fv">Goal amount</label>
              <input
                id="goal-fv"
                className="calc-field__input"
                type="number"
                min={10000}
                max={100000000}
                step={10000}
                value={goal}
                onChange={(e) => setGoal(Number(e.target.value) || 0)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={100000}
              max={20000000}
              step={10000}
              value={Math.min(Math.max(goal, 100000), 20000000)}
              onChange={(e) => setGoal(Number(e.target.value))}
            />
          </div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="goal-r">Expected return (% p.a.)</label>
              <input
                id="goal-r"
                className="calc-field__input"
                type="number"
                min={1}
                max={30}
                step={0.5}
                value={rate}
                onChange={(e) => setRate(Number(e.target.value) || 0)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={1}
              max={30}
              step={0.5}
              value={rate}
              onChange={(e) => setRate(Number(e.target.value))}
            />
          </div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="goal-y">Years to goal</label>
              <input
                id="goal-y"
                className="calc-field__input"
                type="number"
                min={1}
                max={40}
                value={years}
                onChange={(e) => setYears(Number(e.target.value) || 1)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={1}
              max={40}
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
            />
          </div>
        </div>

        <div className="calc-results">
          <div className="calc-result calc-result--accent">
            <span>Monthly SIP needed</span>
            <strong>{formatINR(monthly)}</strong>
          </div>
          <div className="calc-result">
            <span>Total invested</span>
            <strong>{formatINR(invested)}</strong>
          </div>
          <div className="calc-result">
            <span>Est. gains</span>
            <strong>{formatINR(Math.max(0, gains))}</strong>
          </div>
          <p className="calc-note">
            Reverse SIP formula: monthly amount required so FV equals your goal.
          </p>
        </div>
      </div>
    </section>
  );
}
